import React, { useEffect, useState } from 'react';
import './Login.css';
import { apiFetch, ApiError } from './api';

// Tela de redefinição de senha (SEF-70). Acessada via link do e-mail:
// /redefinir-senha?token=...  (roteada pelo App.js, sem login).
//
// Fluxo: valida o token (GET /reset-password/validate), mostra formulário,
// envia nova senha (POST /reset-password), trata WEAK_PASSWORD/INVALID_RESET_TOKEN.

const MOTIVOS = {
  EXPIRED: 'Este link expirou. Solicite um novo link de recuperação.',
  USED: 'Este link já foi utilizado. Solicite um novo link de recuperação.',
  INVALID: 'Link inválido. Solicite um novo link de recuperação.',
};

function RedefinirSenha({ token, onConcluido }) {
  const [validando, setValidando] = useState(true);
  const [valido, setValido] = useState(false);
  const [emailMascarado, setEmailMascarado] = useState('');
  const [motivo, setMotivo] = useState(null);

  const [novaSenha, setNovaSenha] = useState('');
  const [confirmar, setConfirmar] = useState('');
  const [senhaVisivel, setSenhaVisivel] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState('');
  const [concluido, setConcluido] = useState(false);

  const [politica, setPolitica] = useState(null);

  useEffect(() => {
    let ativo = true;
    async function validar() {
      if (!token) {
        if (ativo) {
          setValidando(false);
          setValido(false);
          setMotivo('INVALID');
        }
        return;
      }
      try {
        const r = await apiFetch(
          `/api/auth/reset-password/validate?token=${encodeURIComponent(token)}`,
          { auth: false, retry: false },
        );
        if (!ativo) return;
        setValido(r.valido === true);
        setEmailMascarado(r.email || '');
        setMotivo(r.motivo || (r.valido ? null : 'INVALID'));
      } catch {
        if (ativo) {
          setValido(false);
          setMotivo('INVALID');
        }
      } finally {
        if (ativo) setValidando(false);
      }
    }
    validar();
    apiFetch('/api/auth/politica-senha', { auth: false, retry: false })
      .then((p) => ativo && setPolitica(p))
      .catch(() => {});
    return () => {
      ativo = false;
    };
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErro('');
    if (novaSenha !== confirmar) {
      setErro('As senhas não coincidem.');
      return;
    }
    setEnviando(true);
    try {
      await apiFetch('/api/auth/reset-password', {
        method: 'POST',
        auth: false,
        retry: false,
        body: { token, novaSenha },
      });
      setConcluido(true);
    } catch (error) {
      if (error instanceof ApiError && error.code === 'WEAK_PASSWORD') {
        const regras = error.details?.regras;
        setErro(
          Array.isArray(regras) && regras.length
            ? `Senha fraca: ${regras.join('; ')}`
            : 'Senha fraca. Use ao menos 8 caracteres, com letras e números.',
        );
      } else if (
        error instanceof ApiError &&
        error.code === 'INVALID_RESET_TOKEN'
      ) {
        setValido(false);
        setMotivo('INVALID');
      } else {
        setErro('Não foi possível redefinir a senha. Tente novamente.');
      }
    } finally {
      setEnviando(false);
    }
  };

  const irParaLogin = () => {
    if (typeof onConcluido === 'function') onConcluido();
  };

  return (
    <div className="login-page">
      <div className="login-branding">
        <div className="login-branding-content">
          <img src="/logo.png" alt="Sefira" className="login-brand-logo" />
          <h1 className="login-brand-title">
            Gestao inteligente de<br />propriedade intelectual
          </h1>
          <p className="login-brand-subtitle">
            Plataforma completa para escritorios de advocacia especializados em marcas e patentes.
          </p>
        </div>
        <div className="login-brand-pattern"></div>
      </div>

      <div className="login-form-side">
        <div className="login-form-container">
          <div className="login-form-header">
            <h2>Redefinir senha</h2>
            <p>Escolha uma nova senha para sua conta</p>
          </div>

          {validando && (
            <div className="login-loading">
              <div className="login-spinner"></div>
              Validando link...
            </div>
          )}

          {!validando && !valido && !concluido && (
            <>
              <div className="login-error">
                <i className="fa-solid fa-circle-exclamation"></i>
                {MOTIVOS[motivo] || MOTIVOS.INVALID}
              </div>
              <button
                type="button"
                className="login-submit"
                onClick={irParaLogin}
              >
                Voltar ao login
              </button>
            </>
          )}

          {!validando && valido && !concluido && (
            <>
              {emailMascarado && (
                <p className="login-modal-texto">
                  Redefinindo a senha de <strong>{emailMascarado}</strong>.
                </p>
              )}
              {erro && (
                <div className="login-error">
                  <i className="fa-solid fa-circle-exclamation"></i>
                  {erro}
                </div>
              )}
              <form onSubmit={handleSubmit} className="login-form">
                <div className="login-field">
                  <label htmlFor="nova-senha">Nova senha</label>
                  <div className="login-input-wrapper">
                    <i className="fa-solid fa-lock login-input-icon"></i>
                    <input
                      type={senhaVisivel ? 'text' : 'password'}
                      id="nova-senha"
                      value={novaSenha}
                      onChange={(e) => setNovaSenha(e.target.value)}
                      placeholder="Digite a nova senha"
                      required
                    />
                    <button
                      type="button"
                      className="login-toggle-password"
                      onClick={() => setSenhaVisivel(!senhaVisivel)}
                      tabIndex={-1}
                    >
                      <i className={`fa-solid ${senhaVisivel ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                    </button>
                  </div>
                </div>

                <div className="login-field">
                  <label htmlFor="confirmar-senha">Confirmar nova senha</label>
                  <div className="login-input-wrapper">
                    <i className="fa-solid fa-lock login-input-icon"></i>
                    <input
                      type={senhaVisivel ? 'text' : 'password'}
                      id="confirmar-senha"
                      value={confirmar}
                      onChange={(e) => setConfirmar(e.target.value)}
                      placeholder="Repita a nova senha"
                      required
                    />
                  </div>
                </div>

                {politica?.regras?.length > 0 && (
                  <ul className="login-politica-senha">
                    {politica.regras.map((regra) => (
                      <li key={regra}>{regra}</li>
                    ))}
                  </ul>
                )}

                <button
                  type="submit"
                  className={`login-submit ${enviando ? 'loading' : ''}`}
                  disabled={enviando}
                >
                  {enviando ? (
                    <>
                      <div className="login-spinner"></div>
                      Salvando...
                    </>
                  ) : (
                    'Redefinir senha'
                  )}
                </button>
              </form>
            </>
          )}

          {concluido && (
            <div className="login-modal-sucesso">
              <i className="fa-solid fa-circle-check"></i>
              <h3>Senha redefinida</h3>
              <p>
                Sua senha foi alterada com sucesso. Por segurança, todas as
                sessões anteriores foram encerradas.
              </p>
              <button
                type="button"
                className="login-submit"
                onClick={irParaLogin}
              >
                Ir para o login
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default RedefinirSenha;
